/**
 * Step 3 — WAN & LACP.
 * - WAN1 (required) + WAN2 (optional): DHCP / Static / PPPoE, optionally on a tagged VLAN.
 * - LACP aggregates list editor with multi-port chip picker + optional native IP.
 * - Cross-field collision validators (port overlap, WAN-VLAN clash, aggregate membership).
 */
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, AlertTriangle, Link2, Cable, Eye, EyeOff } from "lucide-react";
import { useWizardStore } from "../state/wizardStore";
import { formatIPInput, validateIPv4 } from "../utils/validation";
import { getSubnetInfo, maskToCIDR } from "../utils/networkUtils";

const WAN_TYPES = [
    { code: "dhcp",   label: "DHCP",   desc: "Auto-assigned by ISP" },
    { code: "static", label: "Static", desc: "Manual IP + gateway" },
    { code: "pppoe",  label: "PPPoE",  desc: "Username + password" },
];

const TextField = ({ label, value, onChange, placeholder, mono = true, type = "text", readOnly, testId, hint, error }) => (
    <div>
        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">{label}</label>
        <input
            type={type} value={value} readOnly={readOnly}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testId}
            className={[
                "w-full px-3.5 py-2.5 bg-canvas border rounded-md text-[14px] text-text-primary placeholder:text-text-dim focus:outline-none transition-colors disabled:opacity-60",
                mono && "font-mono",
                error ? "border-accent-red focus:border-accent-red" : "border-white/[0.08] focus:border-accent-cyan focus:shadow-glow-cyan",
            ].filter(Boolean).join(" ")}
        />
        {hint && !error && <p className="mt-1 text-[10.5px] text-text-dim font-mono">{hint}</p>}
        {error && <p className="mt-1 text-[10.5px] text-accent-red font-mono"><AlertTriangle className="inline w-3 h-3 mr-1" />{error}</p>}
    </div>
);

const SubnetInfoStrip = ({ ip, mask }) => {
    const info = useMemo(() => {
        if (!validateIPv4(ip) || !validateIPv4(mask)) return null;
        try { return getSubnetInfo(ip, mask); } catch { return null; }
    }, [ip, mask]);
    if (!info) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10.5px] font-mono text-text-dim">
            <span><span className="text-text-muted">Net:</span> {info.network}</span>
            <span><span className="text-text-muted">Bcast:</span> {info.broadcast}</span>
            <span><span className="text-text-muted">Range:</span> {info.firstUsable}–{info.lastUsable}</span>
            <span><span className="text-text-muted">Hosts:</span> {info.usableHosts.toLocaleString()}</span>
            <span><span className="text-text-muted">CIDR:</span> /{info.cidr}</span>
        </div>
    );
};

const WanBlock = ({ idx, wan, setWan, availablePorts, otherWanPort, mgmtPort, lacpPorts, vlanIds, error, setError, readOnly }) => {
    const [showPw, setShowPw] = useState(false);
    const set = (patch) => !readOnly && setWan(patch);

    const portInUse = (p) => p === mgmtPort || p === otherWanPort || lacpPorts.includes(p);
    const portOptions = availablePorts.filter((p) => !portInUse(p.name) || p.name === wan.physical_port);

    // Compute the VLAN-collision error inline (pure render-time derivation).
    // Then push it to the store via a guarded effect — never fire if value unchanged.
    // This avoids the infinite render loop caused by recomputing setError every render
    // and feeding it back as a dep.
    const computedError = React.useMemo(() => {
        if (!wan.is_vlan) return null;
        const vid = (wan.vlan_id || "").trim();
        if (!vid) return null;
        const n = Number(vid);
        if (n < 1 || n > 4094) return "VLAN ID must be 1–4094";
        if (vlanIds.includes(vid)) return `VLAN ${vid} is already used by a user VLAN`;
        return null;
    }, [wan.is_vlan, wan.vlan_id, vlanIds]);
    const lastPushedRef = React.useRef(undefined);
    React.useEffect(() => {
        if (lastPushedRef.current === computedError) return;
        lastPushedRef.current = computedError;
        setError(computedError);
        // setError intentionally NOT in deps — it's recomputed by the parent every
        // render. The ref-guard ensures we only push when computedError actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedError]);

    return (
        <div className="rounded-xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-5" data-testid={`wan-block-${idx}`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-[15px] flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-accent-cyan" /> WAN{idx}
                    {idx === 2 && <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">optional</span>}
                </h3>
                {idx === 2 && (
                    <label className="inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
                        <input
                            type="checkbox" disabled={readOnly}
                            checked={!!wan.enabled}
                            onChange={(e) => set({ enabled: e.target.checked })}
                            data-testid="wan2-enabled"
                            className="accent-accent-cyan"
                        />
                        Enable WAN2
                    </label>
                )}
            </div>

            {(idx === 1 || wan.enabled) && (
                <div className="space-y-4">
                    {/* Connection type segmented */}
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-2">Connection type</label>
                        <div className="flex gap-1 p-1 rounded-md bg-canvas border border-white/[0.06] w-fit" role="tablist">
                            {WAN_TYPES.map((t) => (
                                <button key={t.code} type="button" disabled={readOnly}
                                    onClick={() => set({ type: t.code })}
                                    data-testid={`wan${idx}-type-${t.code}`}
                                    className={["px-4 py-1.5 rounded text-[12.5px] font-mono transition-colors",
                                        wan.type === t.code ? "bg-accent-cyan text-canvas" : "text-text-muted hover:text-text-primary",
                                    ].join(" ")}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Physical port */}
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Physical port</label>
                        <select disabled={readOnly} value={wan.physical_port || ""} onChange={(e) => set({ physical_port: e.target.value })} data-testid={`wan${idx}-port`}
                            className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan">
                            <option value="">Select port…</option>
                            {portOptions.map((p) => (<option key={p.name} value={p.name}>{p.name} · {p.speed}{p.type === "wan" ? " (WAN)" : ""}</option>))}
                        </select>
                    </div>

                    {/* VLAN-tagged WAN */}
                    <label className="inline-flex items-center gap-2 text-[12.5px] text-text-muted cursor-pointer">
                        <input type="checkbox" disabled={readOnly} checked={!!wan.is_vlan} onChange={(e) => set({ is_vlan: e.target.checked })} data-testid={`wan${idx}-isvlan`} className="accent-accent-cyan" />
                        WAN delivered as tagged VLAN on a trunk
                    </label>
                    {wan.is_vlan && (
                        <TextField label="VLAN ID" value={wan.vlan_id || ""} onChange={(v) => set({ vlan_id: v.replace(/[^0-9]/g, "").slice(0, 4) })} placeholder="200" testId={`wan${idx}-vlanid`} error={error} readOnly={readOnly} />
                    )}

                    {/* Static fields */}
                    {wan.type === "static" && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <TextField label="IP" value={wan.ip || ""} onChange={(v) => set({ ip: formatIPInput(v) })} placeholder="5.4.3.2" testId={`wan${idx}-ip`} readOnly={readOnly} />
                            <TextField label="Gateway" value={wan.gateway || ""} onChange={(v) => set({ gateway: formatIPInput(v) })} placeholder="5.4.3.1" testId={`wan${idx}-gw`} readOnly={readOnly} />
                            <TextField label="Subnet" value={wan.subnet || ""} onChange={(v) => set({ subnet: formatIPInput(v) })} placeholder="255.255.255.252" testId={`wan${idx}-subnet`} readOnly={readOnly} />
                            <div className="md:col-span-3"><SubnetInfoStrip ip={wan.ip} mask={wan.subnet} /></div>
                        </div>
                    )}

                    {/* PPPoE */}
                    {wan.type === "pppoe" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <TextField label="Username" value={wan.username || ""} onChange={(v) => set({ username: v })} placeholder="ppp-user" testId={`wan${idx}-user`} readOnly={readOnly} mono={false} />
                            <div>
                                <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Password</label>
                                <div className="relative">
                                    <input type={showPw ? "text" : "password"} value={wan.password || ""} readOnly={readOnly} onChange={(e) => set({ password: e.target.value })} placeholder="••••••••" data-testid={`wan${idx}-pass`}
                                        className="w-full px-3.5 py-2.5 pr-10 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan" />
                                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-dim hover:text-text-primary" tabIndex={-1}>
                                        {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const AggregateRow = ({ agg, index, availableLanPorts, allUsedPorts, onChange, onRemove, readOnly }) => {
    const set = (patch) => !readOnly && onChange({ ...agg, ...patch });

    const togglePort = (port) => {
        if (readOnly) return;
        const members = agg.member_ports || [];
        const has = members.includes(port);
        const next = has ? members.filter((p) => p !== port) : [...members, port];
        set({ member_ports: next });
    };

    const error = useMemo(() => {
        if (!agg.name || !/^[a-z0-9-]{2,16}$/.test(agg.name)) return "Name must be 2-16 lowercase chars/digits/hyphens";
        if ((agg.member_ports || []).length < 2) return "At least 2 member ports required";
        return null;
    }, [agg]);

    return (
        <div className="rounded-lg border border-white/[0.06] bg-surface/50 p-4 space-y-3" data-testid={`aggregate-${index}`}>
            <div className="flex items-start gap-3">
                <TextField label="Name" value={agg.name} onChange={(v) => set({ name: v.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 16) })} placeholder="agg1" testId={`agg-${index}-name`} readOnly={readOnly} error={error?.startsWith("Name") ? error : null} />
                <button type="button" disabled={readOnly} onClick={onRemove} data-testid={`agg-${index}-remove`}
                    className="mt-7 text-text-dim hover:text-accent-red transition-colors p-2" aria-label="Remove aggregate">
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Member ports (min 2)</label>
                <div className="flex flex-wrap gap-1.5">
                    {availableLanPorts.map((p) => {
                        const used = allUsedPorts.has(p.name) && !(agg.member_ports || []).includes(p.name);
                        const selected = (agg.member_ports || []).includes(p.name);
                        return (
                            <button key={p.name} type="button" disabled={readOnly || used} onClick={() => togglePort(p.name)} data-testid={`agg-${index}-port-${p.name}`}
                                className={["px-2.5 py-1 rounded-md text-[11.5px] font-mono border transition-colors",
                                    selected ? "bg-accent-cyan/20 border-accent-cyan/50 text-accent-cyan" :
                                    used ? "bg-white/[0.02] border-white/[0.04] text-text-dim cursor-not-allowed line-through" :
                                    "bg-canvas border-white/[0.08] text-text-muted hover:border-accent-cyan/30",
                                ].join(" ")}>
                                {p.name}
                            </button>
                        );
                    })}
                </div>
                {error?.includes("ports") && <p className="mt-1.5 text-[10.5px] text-accent-red font-mono"><AlertTriangle className="inline w-3 h-3 mr-1" />{error}</p>}
            </div>

            <label className="inline-flex items-center gap-2 text-[12.5px] text-text-muted cursor-pointer">
                <input type="checkbox" disabled={readOnly} checked={!!agg.has_native_ip} onChange={(e) => set({ has_native_ip: e.target.checked })} className="accent-accent-cyan" />
                Set a routed IP directly on this LAG (untagged)
            </label>

            {agg.has_native_ip && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Native IP" value={agg.native_ip || ""} onChange={(v) => set({ native_ip: formatIPInput(v) })} placeholder="172.16.99.1" testId={`agg-${index}-nip`} readOnly={readOnly} />
                    <TextField label="Subnet mask" value={agg.native_subnet || ""} onChange={(v) => set({ native_subnet: formatIPInput(v) })} placeholder="255.255.255.0" testId={`agg-${index}-nmask`} readOnly={readOnly} />
                    <div className="md:col-span-2"><SubnetInfoStrip ip={agg.native_ip} mask={agg.native_subnet} /></div>
                </div>
            )}
        </div>
    );
};

export const Step3WanLacp = ({ hardwareData, readOnly = false }) => {
    const {
        vendor, modello, mgmt_port,
        wan1, wan2, setWAN1, setWAN2,
        wan1_vlan_error, wan2_vlan_error, setWanVlanError,
        lacp_aggregates, setLACPAggregates,
        vlans,
    } = useWizardStore();

    const currentModel = useMemo(() => {
        const v = hardwareData?.vendors?.find((x) => x.vendorCode === vendor);
        return v?.models?.find((m) => m.name === modello) || null;
    }, [hardwareData, vendor, modello]);
    const allPorts = currentModel?.ports || [];

    const lanPorts = allPorts.filter((p) => p.type === "lan" || p.type === "sfp" || p.type === "combo" || p.type === "dmz");
    const wanCapablePorts = allPorts.filter((p) => p.type === "wan" || p.type === "lan");

    const lacpMemberPorts = useMemo(
        () => new Set(lacp_aggregates.flatMap((a) => a.member_ports || [])),
        [lacp_aggregates],
    );
    const vlanIds = useMemo(() => vlans.map((v) => String(v.vlan_id)), [vlans]);

    const addAggregate = () => {
        if (readOnly) return;
        const used = new Set(lacp_aggregates.map((a) => a.name));
        let n = 1; while (used.has(`agg${n}`)) n++;
        setLACPAggregates([...lacp_aggregates, {
            name: `agg${n}`, member_ports: [], has_native_ip: false, native_ip: "", native_subnet: "",
        }]);
    };
    const updateAggregate = (idx, next) => {
        const copy = [...lacp_aggregates]; copy[idx] = next; setLACPAggregates(copy);
    };
    const removeAggregate = (idx) => {
        const copy = lacp_aggregates.filter((_, i) => i !== idx); setLACPAggregates(copy);
    };

    return (
        <div className="space-y-6" data-testid="step3-wan-lacp">
            <header>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Step 3 of 7</p>
                <h2 className="mt-2 font-display font-semibold text-3xl tracking-tight">WAN & link aggregation</h2>
                <p className="mt-2 text-[13.5px] text-text-muted max-w-2xl">
                    Configure your uplinks (WAN1, optional WAN2) and any LACP aggregates for high availability or bandwidth bundling.
                </p>
            </header>

            {/* Quick recap chips */}
            <div className="flex flex-wrap gap-2" data-testid="wan-summary">
                <span className="px-3 py-1 rounded-md bg-canvas border border-white/[0.06] text-[11.5px] font-mono text-text-muted">
                    WAN1: <span className="text-accent-cyan">{wan1.type.toUpperCase()}</span> on {wan1.physical_port || "—"}
                    {wan1.is_vlan && wan1.vlan_id && <> · VLAN {wan1.vlan_id}</>}
                </span>
                {wan2.enabled && (
                    <span className="px-3 py-1 rounded-md bg-canvas border border-white/[0.06] text-[11.5px] font-mono text-text-muted">
                        WAN2: <span className="text-accent-cyan">{wan2.type.toUpperCase()}</span> on {wan2.physical_port || "—"}
                        {wan2.is_vlan && wan2.vlan_id && <> · VLAN {wan2.vlan_id}</>}
                    </span>
                )}
                {lacp_aggregates.length > 0 && (
                    <span className="px-3 py-1 rounded-md bg-canvas border border-white/[0.06] text-[11.5px] font-mono text-text-muted">
                        {lacp_aggregates.length} aggregate{lacp_aggregates.length > 1 ? "s" : ""}
                    </span>
                )}
            </div>

            <WanBlock idx={1} wan={wan1} setWan={setWAN1}
                availablePorts={wanCapablePorts} otherWanPort={wan2.enabled ? wan2.physical_port : ""} mgmtPort={mgmt_port}
                lacpPorts={Array.from(lacpMemberPorts)} vlanIds={vlanIds}
                error={wan1_vlan_error} setError={(e) => setWanVlanError(1, e)} readOnly={readOnly} />

            <WanBlock idx={2} wan={wan2} setWan={setWAN2}
                availablePorts={wanCapablePorts} otherWanPort={wan1.physical_port} mgmtPort={mgmt_port}
                lacpPorts={Array.from(lacpMemberPorts)} vlanIds={vlanIds}
                error={wan2_vlan_error} setError={(e) => setWanVlanError(2, e)} readOnly={readOnly} />

            {/* LACP section */}
            <section className="rounded-xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-semibold text-[15px] flex items-center gap-2">
                        <Cable className="w-4 h-4 text-accent-cyan" /> LACP aggregates
                    </h3>
                    <button type="button" disabled={readOnly} onClick={addAggregate} data-testid="aggregate-add"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan text-[12.5px] hover:bg-accent-cyan/20 transition-colors disabled:opacity-50">
                        <Plus className="w-3.5 h-3.5" /> Add aggregate
                    </button>
                </div>
                {lacp_aggregates.length === 0 ? (
                    <div className="rounded-md border border-dashed border-white/[0.08] bg-canvas/30 p-5 text-center text-[12.5px] text-text-muted">
                        No aggregates yet. Add one to bundle ports for redundancy + bandwidth.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {lacp_aggregates.map((a, i) => {
                            const otherUsed = new Set(
                                lacp_aggregates.filter((_, idx) => idx !== i).flatMap((x) => x.member_ports || [])
                            );
                            if (mgmt_port) otherUsed.add(mgmt_port);
                            if (wan1.physical_port) otherUsed.add(wan1.physical_port);
                            if (wan2.enabled && wan2.physical_port) otherUsed.add(wan2.physical_port);
                            return (
                                <AggregateRow key={i} agg={a} index={i}
                                    availableLanPorts={lanPorts} allUsedPorts={otherUsed}
                                    onChange={(next) => updateAggregate(i, next)} onRemove={() => removeAggregate(i)} readOnly={readOnly} />
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};

export function isStep3Valid(state) {
    if (!state.wan1?.physical_port) return false;
    if (state.wan1.is_vlan && !state.wan1.vlan_id) return false;
    if (state.wan1.type === "static" && (!validateIPv4(state.wan1.ip) || !validateIPv4(state.wan1.gateway) || !validateIPv4(state.wan1.subnet))) return false;
    if (state.wan1.type === "pppoe" && (!state.wan1.username || !state.wan1.password)) return false;
    if (state.wan2?.enabled) {
        if (!state.wan2.physical_port) return false;
        if (state.wan2.is_vlan && !state.wan2.vlan_id) return false;
        if (state.wan2.type === "static" && (!validateIPv4(state.wan2.ip) || !validateIPv4(state.wan2.gateway))) return false;
    }
    if (state.wan1_vlan_error || state.wan2_vlan_error) return false;
    return true;
}

export default Step3WanLacp;
